import React, { useState, useEffect, useContext } from 'react';
import api from '../utils/api';
import { UserContext } from '../App';
import { useNavigate, useLocation } from 'react-router-dom';
import { FINANCIAL_CATEGORIES } from './FinancialAssets';
import Logo from './Logo';

export default function Dashboard() {
  // All hooks must be called at the top level, unconditionally
  const { currentUser, users, setCurrentUser } = useContext(UserContext);
  // Nominee view state
  const [beneficiaryOwners, setBeneficiaryOwners] = useState([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [assets, setAssets] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Chart states
  const [chartLevel, setChartLevel] = useState('category');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [hoveredSlice, setHoveredSlice] = useState(null);
  
  // Flag to prevent multiple runs of the same effect
  const [hasInitializedOwners, setHasInitializedOwners] = useState(false);
  const [hasCheckedLocalStorage, setHasCheckedLocalStorage] = useState(false);

  // Asset owner is the current logged-in user
  const assetHolder = currentUser;
  if (!assetHolder) {
    return <div>Loading...</div>;
  }

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset initialization flags when user changes
  useEffect(() => {
    setHasInitializedOwners(false);
    setHasCheckedLocalStorage(false);
  }, [currentUser.email]);

  // Fetch owners for nominee view
  useEffect(() => {
    if (currentUser.type === 'nominee' && !hasInitializedOwners) {
      console.log('Dashboard - Starting to fetch beneficiary owners for:', currentUser.email);
      setHasInitializedOwners(true);
      
      Promise.all([
        api.get('/nominees'),
        api.get('/auth/users')
      ]).then(([benRes, usersRes]) => {
        const allBeneficiaries = benRes.data;
        const allUsers = usersRes.data;
        console.log('Dashboard - All nominees:', allBeneficiaries);
        console.log('Dashboard - All users:', allUsers);
        
        const ownerIds = [...new Set(allBeneficiaries
          .filter(b => b.email === currentUser.email)
          .map(b => b.ownerId))];

        console.log('Dashboard - Found ownerIds for nominee:', ownerIds);

        setBeneficiaryOwners(ownerIds.map(ownerId => ({
          ownerId,
          ownerName: allUsers.find(u => u.id === ownerId)?.name || 'Unknown Owner'
        })));
      });
    } else if (currentUser.type !== 'nominee') {
      // For asset holders, fetch their own assets
      api.get('/assets').then(res => {
        setAssets(res.data.filter(a => a.ownerId === currentUser.id));
      });
    }
  }, [currentUser.email, currentUser.type, hasInitializedOwners]);

  // Handle localStorage check and initial owner selection
  useEffect(() => {
    if (currentUser.type === 'nominee' && beneficiaryOwners.length > 0 && !hasCheckedLocalStorage) {
      console.log('Dashboard - Checking localStorage for selectedOwnerId');
      setHasCheckedLocalStorage(true);
      
      const storedOwnerId = localStorage.getItem('selectedOwnerId');
      console.log('Dashboard - Stored ownerId from localStorage:', storedOwnerId);
      console.log('Dashboard - Available ownerIds:', beneficiaryOwners.map(o => o.ownerId));
      
      if (storedOwnerId && beneficiaryOwners.some(o => o.ownerId === storedOwnerId)) {
        console.log('Dashboard - Setting selectedOwnerId to stored value:', storedOwnerId);
        setSelectedOwnerId(storedOwnerId);
        localStorage.removeItem('selectedOwnerId');
      } else if (beneficiaryOwners.length > 0) {
        console.log('Dashboard - Setting selectedOwnerId to first owner:', beneficiaryOwners[0].ownerId);
        setSelectedOwnerId(beneficiaryOwners[0].ownerId);
      }
    }
  }, [currentUser.type, beneficiaryOwners, hasCheckedLocalStorage]);

  // Fetch assets for selected owner (for nominees)
  useEffect(() => {
    if (currentUser.type === 'nominee' && selectedOwnerId) {
      console.log('Dashboard - Fetching assets for selectedOwnerId:', selectedOwnerId);
      api.get('/assets').then(res => {
        const filteredAssets = res.data.filter(a => a.ownerId === selectedOwnerId);
        console.log('Dashboard - All assets from API:', res.data);
        console.log('Dashboard - Filtered assets for selectedOwnerId:', filteredAssets);
        setAssets(filteredAssets);
      });
    }
  }, [selectedOwnerId, currentUser.type]);

  const getChartData = () => {
    if (currentUser.type === 'nominee' && !selectedOwnerId) return [];
    
    const financialAssets = assets.filter(a => a.typeCategory && ['Bank account', 'Credit Card', 'Investment account', 'Pension', 'Insurance', 'Property'].includes(a.typeCategory));
    
    if (chartLevel === 'category') {
      const categoryGroups = {};
      financialAssets.forEach(asset => {
        const category = asset.typeCategory || 'Other';
        categoryGroups[category] = (categoryGroups[category] || 0) + 1;
      });
      return Object.entries(categoryGroups).map(([name, value]) => ({ name, value }));
    } else if (chartLevel === 'institution') {
      const institutionGroups = {};
      financialAssets.forEach(asset => {
        if (asset.typeCategory === selectedCategory) {
          const institution = asset.institution || 'Other';
          institutionGroups[institution] = (institutionGroups[institution] || 0) + 1;
        }
      });
      return Object.entries(institutionGroups).map(([name, value]) => ({ name, value }));
    }
    return [];
  };

  const handleChartClick = (item) => {
    if (chartLevel === 'category') {
      setSelectedCategory(item.name);
      setChartLevel('institution');
    }
  };

  const resetChart = () => {
    setChartLevel('category');
    setSelectedCategory('');
  };

  function InteractivePieChart({ data, size = 200, stroke = 40, onSliceClick }) {
    if (data.length === 0) return <div style={{ textAlign: 'center', color: '#666', padding: 20 }}>No financial data available</div>;
    
    const total = data.reduce((sum, item) => sum + item.value, 0);
    let currentAngle = 0;
    
    // Ensure single slice is visible by making it a full circle
    const adjustedData = data.length === 1 ? [{ ...data[0], value: total }] : data;
    const adjustedTotal = adjustedData.reduce((sum, item) => sum + item.value, 0);
    
    return (
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <svg width={size} height={size} style={{ display: 'block', margin: '0 auto' }}>
          {adjustedData.map((item, index) => {
            const percentage = item.value / adjustedTotal;
            const angle = percentage * 360;
            const startAngle = currentAngle;
            currentAngle += angle;
            
            const startRad = (startAngle - 90) * Math.PI / 180;
            const endRad = (currentAngle - 90) * Math.PI / 180;
            
            const x1 = size/2 + (size/2 - stroke/2) * Math.cos(startRad);
            const y1 = size/2 + (size/2 - stroke/2) * Math.sin(startRad);
            const x2 = size/2 + (size/2 - stroke/2) * Math.cos(endRad);
            const y2 = size/2 + (size/2 - stroke/2) * Math.sin(endRad);
            
            const largeArcFlag = angle > 180 ? 1 : 0;
            
            const pathData = [
              `M ${x1} ${y1}`,
              `A ${size/2 - stroke/2} ${size/2 - stroke/2} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
              `L ${size/2 + (size/2) * Math.cos(endRad)} ${size/2 + (size/2) * Math.sin(endRad)}`,
              `A ${size/2} ${size/2} 0 ${largeArcFlag} 0 ${size/2 + (size/2) * Math.cos(startRad)} ${size/2 + (size/2) * Math.sin(startRad)}`,
              'Z'
            ].join(' ');
            
            const colors = ['#5c8d78', '#8fb6a3', '#dddbe4', '#e2e7ed', '#ffece3', '#ffd2bf', '#f6f7f9', '#e2e7ed'];
            const color = colors[index % colors.length];
            
            return (
              <path
                key={index}
                d={pathData}
                fill={color}
                stroke="#fff"
                strokeWidth={hoveredSlice === index ? 3 : 1}
                opacity={hoveredSlice === null || hoveredSlice === index ? 1 : 0.7}
                style={{ cursor: 'pointer', transition: 'opacity 0.2s, stroke-width 0.2s' }}
                onMouseEnter={() => setHoveredSlice(index)}
                onMouseLeave={() => setHoveredSlice(null)}
                onClick={() => onSliceClick(item)}
              />
            );
          })}
        </svg>
        
        {/* Legend */}
        <div style={{ 
          marginTop: 16, 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 8,
          maxWidth: size,
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          {adjustedData.map((item, index) => {
            const colors = ['#5c8d78', '#8fb6a3', '#dddbe4', '#e2e7ed', '#ffece3', '#ffd2bf', '#f6f7f9', '#e2e7ed'];
            const color = colors[index % colors.length];
            const percentage = ((item.value / adjustedTotal) * 100).toFixed(1);
            
            return (
              <div key={index} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 8,
                fontSize: 14,
                color: '#333'
              }}>
                <div style={{ 
                  width: 12, 
                  height: 12, 
                  backgroundColor: color, 
                  borderRadius: 2,
                  flexShrink: 0
                }} />
                <span style={{ flex: 1, fontWeight: 500 }}>{item.name}</span>
                <span style={{ fontWeight: 600, color: '#1976d2' }}>
                  {`${item.value} assets (${percentage}%)`}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Calculate totals for nominee view
  const totalAssets = assets.length;
  const financialAssets = assets.filter(a => a.typeCategory && ['Bank account', 'Credit Card', 'Investment account', 'Pension', 'Insurance', 'Property'].includes(a.typeCategory));
  const legalAssets = assets.filter(a => a.typeCategory === 'Legal document');
  const otherAssets = assets.filter(a => !['Bank account', 'Credit Card', 'Investment account', 'Pension', 'Insurance', 'Property', 'Legal document'].includes(a.typeCategory));
  
  const totalFinancialValue = financialAssets.length;
  const chartData = getChartData();
  const currentViewCount = Array.isArray(chartData) ? chartData.reduce((s, x) => s + Number(x.value || 0), 0) : 0;

  // Sidebar styles
  const sidebarStyle = {
    width: 220,
    background: 'var(--dusk-700)',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    padding: '32px 0 18px 0',
    boxShadow: '2px 0 12px rgba(30,40,60,0.04)',
    position: 'fixed',
    left: 0,
    top: 0,
    height: '100vh',
    zIndex: 100,
  };
  const mobileSidebarStyle = {
    ...sidebarStyle,
    width: 180,
    minWidth: 0,
    maxWidth: '80vw',
    transition: 'transform 0.3s',
    transform: 'translateX(-100%)',
  };
  const mobileSidebarOpenStyle = {
    ...mobileSidebarStyle,
    transform: 'translateX(0)',
  };

  const hasNoAssets = assets.length === 0 && currentUser.type !== 'nominee';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--mist-100)' }}>
      {/* Mobile hamburger */}
      {isMobile && (
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{ position: 'fixed', top: 18, left: 18, zIndex: 2100, background: 'var(--sage-600)', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 12px', fontSize: 22, boxShadow: 'var(--shadow-soft)', cursor: 'pointer' }}
          aria-label="Open menu"
        >
          ☰
        </button>
      )}
      {/* Sidebar */}
      <nav
        style={
          isMobile
            ? (sidebarOpen ? mobileSidebarOpenStyle : mobileSidebarStyle)
            : sidebarStyle
        }
      >
        <div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <Logo size={36} />
          </div>
          {/* User indicator */}
          <div style={{ background: 'var(--sage-600)', color: '#fff', borderRadius: 8, padding: '10px 16px', margin: '0 18px 18px 18px', fontSize: 15, fontWeight: 500, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, boxShadow: 'var(--shadow-soft)' }}>
            <span>Logged in as:</span>
            <span style={{ fontWeight: 700 }}>{currentUser.name}</span>
            <span style={{ fontSize: 13, opacity: 0.85 }}>{currentUser.email}</span>
            {currentUser.role && <span style={{ fontSize: 13, opacity: 0.7 }}>({currentUser.role})</span>}
          </div>
          
          {/* Dashboard */}
          <div style={{ marginBottom: 24 }}>
            <button
              onClick={() => { navigate('/dashboard'); setSidebarOpen(false); }}
              style={{
                background: 'var(--sage-600)', color: '#fff', border: 'none', borderRadius: 12, padding: '12px 18px', fontWeight: 600, fontSize: 16, margin: '0 18px', width: 'calc(100% - 36px)', cursor: 'pointer', textAlign: 'left', outline: 'none', boxShadow: 'var(--shadow-soft)'
              }}
            >
              Dashboard
            </button>
          </div>

          {/* Assets Section */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--dusk-500)', margin: '0 18px 8px 18px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Assets</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <button
                onClick={() => { navigate('/financial-assets'); setSidebarOpen(false); }}
                style={{
                  background: location.pathname === '/financial-assets' ? 'var(--sage-600)' : '#29304a',
                  color: '#fff', border: 'none', borderRadius: 12, padding: '12px 18px', fontWeight: location.pathname === '/financial-assets' ? 600 : 500, fontSize: 16, margin: '0 18px', cursor: 'pointer', textAlign: 'left', outline: 'none', boxShadow: location.pathname === '/financial-assets' ? 'var(--shadow-soft)' : 'none'
                }}
              >
                Financial Assets
              </button>
              <button
                onClick={() => { navigate('/legal-assets'); setSidebarOpen(false); }}
                style={{
                  background: location.pathname === '/legal-assets' ? 'var(--sage-600)' : '#29304a',
                  color: '#fff', border: 'none', borderRadius: 12, padding: '12px 18px', fontWeight: location.pathname === '/legal-assets' ? 600 : 500, fontSize: 16, margin: '0 18px', cursor: 'pointer', textAlign: 'left', outline: 'none', boxShadow: location.pathname === '/legal-assets' ? 'var(--shadow-soft)' : 'none'
                }}
              >
                Legal Assets
              </button>
              <button
                onClick={() => { navigate('/other-assets'); setSidebarOpen(false); }}
                style={{
                  background: location.pathname === '/other-assets' ? 'var(--sage-600)' : '#29304a',
                  color: '#fff', border: 'none', borderRadius: 12, padding: '12px 18px', fontWeight: location.pathname === '/other-assets' ? 600 : 500, fontSize: 16, margin: '0 18px', cursor: 'pointer', textAlign: 'left', outline: 'none', boxShadow: location.pathname === '/other-assets' ? 'var(--shadow-soft)' : 'none'
                }}
              >
                Other Assets
              </button>
            </div>
          </div>

          {/* Account Section */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--dusk-500)', margin: '0 18px 8px 18px', textTransform: 'uppercase', letterSpacing: 0.5 }}>Account</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <button
                onClick={() => { navigate('/account'); setSidebarOpen(false); }}
                style={{
                  background: location.pathname === '/account' ? 'var(--sage-600)' : '#29304a',
                  color: '#fff', border: 'none', borderRadius: 12, padding: '12px 18px', fontWeight: location.pathname === '/account' ? 600 : 500, fontSize: 16, margin: '0 18px', cursor: 'pointer', textAlign: 'left', outline: 'none', boxShadow: location.pathname === '/account' ? 'var(--shadow-soft)' : 'none'
                }}
              >
                Account
              </button>
            </div>
          </div>

          
        </div>
        
      </nav>

      {/* Main content */}
      <div style={{ flex: 1, marginLeft: isMobile ? 0 : 220, padding: isMobile ? '60px 0 0 0' : '0 0 0 0', minHeight: '100vh', overflow: 'auto' }}>
        <div style={{ padding: 20, position: 'relative' }}>
          {currentUser.type === 'nominee' ? (
            <>
              {/* Owner selector dropdown */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <h1 style={{ margin: 0 }}>
                  {(() => {
                            const owner = beneficiaryOwners.find(o => o.ownerId === selectedOwnerId);
        if (!owner) return 'Owner\'s Assets';
        return `${owner.ownerName || 'Owner'}'s Assets`;
                  })()}
                </h1>
                <select
                  value={selectedOwnerId}
                  onChange={e => setSelectedOwnerId(e.target.value)}
                  style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16, minWidth: 180 }}
                >
                  {beneficiaryOwners.map(o => (
                    <option key={o.ownerId} value={o.ownerId}>
                      {o.ownerName || o.ownerId}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Asset Summary Banner */}
              <div style={{ background: '#fff', padding: '16px 20px', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--sage-600)' }}>{totalAssets}</div>
                    <div style={{ fontSize: 13, color: '#666', fontWeight: 500 }}>Total Assets</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#4caf50' }}>{financialAssets.length}</div>
                    <div style={{ fontSize: 13, color: '#666', fontWeight: 500 }}>Financial</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#ff9800' }}>{legalAssets.length}</div>
                    <div style={{ fontSize: 13, color: '#666', fontWeight: 500 }}>Legal</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#9c27b0' }}>{otherAssets.length}</div>
                    <div style={{ fontSize: 13, color: '#666', fontWeight: 500 }}>Other</div>
                  </div>
                </div>
              </div>

              {/* Portfolio Summary */}
              <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 4px 24px rgba(30,40,60,0.10)', padding: 32, minHeight: '60vh' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                  <h2 style={{ margin: 0, fontWeight: 700, fontSize: 20, color: '#222' }}>Financial Portfolio</h2>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    {(currentUser.role === 'dual' || currentUser.role === 'nominee') && (
                      <select
                        aria-label="Switch role context"
                        value={currentUser.type === 'nominee' ? 'nominee' : 'owner'}
                        onChange={async (e) => {
                          const next = e.target.value;
                          try {
                            await api.post('/auth/switch-role', { id: currentUser.id, active_context: next });
                            const updated = { ...currentUser, type: next };
                            setCurrentUser(updated);
                            try { localStorage.setItem('currentUser', JSON.stringify(updated)); } catch {}
                          } catch (err) {
                            console.error('Failed to switch role', err?.response?.data || err);
                          }
                        }}
                        style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--mist-300)' }}
                      >
                        <option value="owner">Owner View</option>
                        <option value="nominee">Nominee View</option>
                      </select>
                    )}
                    {chartLevel !== 'category' && (
                      <button
                        onClick={resetChart}
                        style={{
                          padding: '8px 16px',
                          fontSize: 14,
                          background: '#6c757d',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          fontWeight: 500,
                          cursor: 'pointer'
                        }}
                      >
                        Reset View
                      </button>
                    )}
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
                  <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--sage-600)', marginRight: 16 }}>
                    {currentViewCount} assets
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <InteractivePieChart 
                    data={chartData} 
                    size={300} 
                    stroke={50} 
                    onSliceClick={handleChartClick}
                  />
                </div>
              </div>
            </>
          ) : (
            // Original owner dashboard content
            <>
              {hasNoAssets && (
                <div style={{ background: '#fff', padding: 24, borderRadius: 12, boxShadow: '0 4px 24px rgba(30,40,60,0.10)', marginBottom: 24 }}>
                  <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Let’s get you protected.</h2>
                  <p style={{ margin: '8px 0 16px 0', color: '#555' }}>Add your first asset to your vault.</p>
                  <button
                    onClick={() => navigate('/financial-assets')}
                    style={{ background: 'var(--sage-600)', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 16px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Add your first asset
                  </button>
                </div>
              )}
              {/* Quick Actions Dropdown */}
            <div style={{ position: 'absolute', top: 20, right: 20, zIndex: 100 }}>
              <button onClick={() => setQuickActionsOpen(open => !open)} style={{ background: 'var(--sage-600)', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 16px', fontSize: 15, fontWeight: 600, cursor: 'pointer', boxShadow: 'var(--shadow-soft)' }}>
                  Quick Actions ▼
                </button>
                {quickActionsOpen && (
                  <div style={{ position: 'absolute', right: 0, top: 45, background: '#fff', color: '#222b45', borderRadius: 8, boxShadow: '0 4px 16px rgba(30,40,60,0.13)', minWidth: 200, zIndex: 10, padding: '8px 0' }}>
                    <button
                      onClick={() => { navigate('/financial-assets'); setQuickActionsOpen(false); }}
                      style={{ width: '100%', background: 'none', border: 'none', color: '#222b45', fontWeight: 500, fontSize: 15, padding: '10px 18px', textAlign: 'left', cursor: 'pointer', borderRadius: 6, transition: 'background 0.15s' }}
                      onMouseOver={e => e.currentTarget.style.background = '#f0f0f0'}
                      onMouseOut={e => e.currentTarget.style.background = 'none'}
                    >
                      Add Financial Assets
                    </button>
                    <button
                      onClick={() => { navigate('/legal-assets'); setQuickActionsOpen(false); }}
                      style={{ width: '100%', background: 'none', border: 'none', color: '#222b45', fontWeight: 500, fontSize: 15, padding: '10px 18px', textAlign: 'left', cursor: 'pointer', borderRadius: 6, transition: 'background 0.15s' }}
                      onMouseOver={e => e.currentTarget.style.background = '#f0f0f0'}
                      onMouseOut={e => e.currentTarget.style.background = 'none'}
                    >
                      Add Legal Assets
                    </button>
                    <button
                      onClick={() => { navigate('/other-assets'); setQuickActionsOpen(false); }}
                      style={{ width: '100%', background: 'none', border: 'none', color: '#222b45', fontWeight: 500, fontSize: 15, padding: '10px 18px', textAlign: 'left', cursor: 'pointer', borderRadius: 6, transition: 'background 0.15s' }}
                      onMouseOver={e => e.currentTarget.style.background = '#f0f0f0'}
                      onMouseOut={e => e.currentTarget.style.background = 'none'}
                    >
                      Add Other Assets
                    </button>
                  </div>
                )}
              </div>

              {/* Welcome Banner */}
              <div style={{ background: 'linear-gradient(135deg, var(--sage-600) 0%, var(--sage-400) 100%)', color: '#fff', padding: 16, borderRadius: 16, marginBottom: 24, boxShadow: 'var(--shadow-soft)' }}>
                <h1 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Welcome to ProVault</h1>
                <p style={{ margin: '4px 0 0 0', fontSize: 13, opacity: 0.9 }}>Your comprehensive wealth management and probate planning platform</p>
              </div>

              {/* Asset Summary Banner */}
              <div style={{ background: '#fff', padding: '16px 20px', borderRadius: 16, boxShadow: 'var(--shadow-soft)', border: '1px solid var(--mist-200)', marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--sage-600)' }}>{assets.length}</div>
                     <div style={{ fontSize: 13, color: 'var(--dusk-500)', fontWeight: 500 }}>Total Assets</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#4caf50' }}>{financialAssets.length}</div>
                     <div style={{ fontSize: 13, color: 'var(--dusk-500)', fontWeight: 500 }}>Financial</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#ff9800' }}>{legalAssets.length}</div>
                     <div style={{ fontSize: 13, color: 'var(--dusk-500)', fontWeight: 500 }}>Legal</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#9c27b0' }}>{otherAssets.length}</div>
                     <div style={{ fontSize: 13, color: 'var(--dusk-500)', fontWeight: 500 }}>Other</div>
                  </div>
                </div>
              </div>

              {/* Portfolio Summary */}
              <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 4px 24px rgba(30,40,60,0.10)', padding: 32, minHeight: '60vh' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                  <h2 style={{ margin: 0, fontWeight: 700, fontSize: 20, color: '#222' }}>Financial Portfolio</h2>
                  {chartLevel !== 'category' && (
                    <button
                      onClick={resetChart}
                      style={{
                        padding: '8px 16px',
                        fontSize: 14,
                        background: '#6c757d',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        fontWeight: 500,
                        cursor: 'pointer'
                      }}
                    >
                      Reset View
                    </button>
                  )}
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
                  <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--sage-600)', marginRight: 16 }}>
                    {currentViewCount} assets
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <InteractivePieChart 
                    data={chartData} 
                    size={300} 
                    stroke={50} 
                    onSliceClick={handleChartClick}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}